import { useEffect, useState, useCallback } from "react";
import { useParams } from "react-router-dom";
import useCoreStore from "views/hooks/useCoreStore";
import useProductApi from "./useProductApi";

interface ProductFilters {
  category?: ID;
  company: ID;
  name_Istartswith?: string;
  reference_Istartswith?: string;
}

const PAGINATION_INIT = {
  offset: 0,
  limit: 10,
};

export default function useProductsTable() {
  const urlParams: RouteParams = useParams();

  const setCategoryId = useCoreStore(state => state.setCategoryId);
  const categoryId = useCoreStore(state => state.categoryId);

  const FILTER_INIT: ProductFilters = {
    category: undefined,
    company: urlParams.companyId,
  };

  const [filters, setFilters] = useState<ProductFilters>(FILTER_INIT);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const {
    listProducts,
    dataSource,
    loading,
    refetch,
    totalCount,
    listCategories,
    categoryOptions,
  } = useProductApi(urlParams.clientId);

  useEffect(() => {
    if (urlParams?.companyId) {
      setFilters({ company: urlParams.companyId });

      listCategories({
        variables: {
          filter: { company: urlParams.companyId },
        },
      });

      listProducts({
        variables: {
          filter: { company: urlParams.companyId },
          limit: 0,
          offset: 0,
        },
      });
    }
  }, [urlParams?.companyId]);

  // Filter by category when categoryId changes
  useEffect(() => {
    if (categoryId) {
      setFilters({
        company: urlParams?.companyId,
        category: categoryId,
      });

      listProducts({
        variables: {
          filter: {
            company: urlParams?.companyId,
            category: categoryId,
          },
          limit: 0,
          offset: 0,
        },
      });
    }
  }, [categoryId]);

  const handleSearchFilters = useCallback(
    (confirm: () => void) => {
      confirm();
      listProducts({
        variables: {
          filter: filters,
          ...PAGINATION_INIT,
        },
      });
    },
    [filters, listProducts],
  );

  const handleResetFilter = useCallback(
    (_filterList: unknown, confirm: () => void) => {
      confirm();
      setCategoryId(undefined);
      setFilters(FILTER_INIT);
      listProducts({
        variables: {
          filter: FILTER_INIT,
          ...PAGINATION_INIT,
        },
      });
    },
    [FILTER_INIT, listProducts, setCategoryId],
  );

  const handlePagination = useCallback(
    (page: number, size: number) => {
      setPageSize(size);
      setCurrentPage(page);
      refetch({
        limit: size,
        offset: size * (page - 1),
      });
    },
    [refetch],
  );

  return {
    dataSource,
    loading,
    totalCount,
    categoryOptions,
    currentPage,
    pageSize,
    handlePagination,
    filters,
    setFilters,
    handleSearchFilters,
    handleResetFilter,
    refetch,
  };
}
